import {
  ACCOUNT_TRANSACTION_CLASSIFICATIONS,
  ACCOUNT_TRANSACTION_DIRECTIONS,
  ACCOUNT_TRANSACTION_SOURCES,
  CANDIDATE_READINGS,
  CARD_PROVIDERS,
  CLEARING_READINGS,
  currencySchema,
  donationStatusSchema,
  EXCLUSION_CLASSIFICATIONS,
  FUND_TRANSFER_STATUSES,
  INFORMATION_HIT_REASONS,
  MANUAL_PAYMENT_METHODS,
  MANUAL_PAYMENT_STAGES,
  MATCH_CLOSED_REASONS,
  OUTGOING_CLASSIFICATIONS,
  paymentStatusSchema,
  RECONCILIATION_METHODS,
  REFUND_STATUSES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { bulkResultSchema, bulkRowSchema } from './common/list.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { queryBooleanSchema } from './common/query.js';
import { generatedFileSchema } from './generated-file.js';
import {
  accountTransactionListItemSchema,
  movementAmountFilterShape,
  movementEquivalentsSchema,
  refineMovementAmountFilter,
} from './statement.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

const reasonSchema = z.string().trim().min(1).max(500);

const positiveMinorSchema = z
  .string()
  .regex(/^\d{1,18}$/, 'must be an integer in minor units')
  .refine((value) => /[1-9]/.test(value), 'must be above zero');

const nonNegativeMinorSchema = z.string().regex(/^\d{1,18}$/, 'must be an integer in minor units');

const signedMinorSchema = z.string().regex(/^-?\d{1,18}$/, 'must be an integer in minor units');

export const RECONCILIATION_STATUSES = [
  'pending_approval',
  'confirmed',
  'rejected',
  'reversed',
] as const;

/**
 * Why a match waits for a second person: a conversion nobody saw on a
 * statement, or a payment typed by hand — docs/decisions/0100.
 */
export const RECONCILIATION_APPROVAL_REASONS = ['cross_currency', 'manual_payment'] as const;

export const RECONCILIATION_REVERSAL_KINDS = [
  'unmatch',
  'cancel_refund',
  'cancel_reverse',
  'chargeback',
  'provider_refund',
] as const;

/**
 * The tiers a person may claim for a match. `provider` is absent: only a
 * verified provider callback confirms a card capture, never a request.
 */
export const claimableReconciliationMethodSchema = z
  .enum(RECONCILIATION_METHODS)
  .exclude(['provider']);

export const MAX_ALLOCATIONS_PER_MATCH = 50;

/**
 * One allocation of part of a line to part of a donation. Both sides carry
 * their own currency; `rateDate` is set only when they differ, and names the
 * bulletin day both rate rows came from.
 */
export const reconciliationSchema = z.object({
  id: z.uuid(),
  accountTransactionId: z.uuid(),
  donationId: z.uuid(),
  organizationId: z.uuid(),
  transactionAmount: moneySchema,
  donationAmount: moneySchema,
  rateDate: isoDaySchema.nullable(),
  /** What the match absorbed inside the FX tolerance, signed, in the donation's currency — docs/decisions/0128. */
  fxDifferenceMinor: signedMinorSchema,
  method: z.enum(RECONCILIATION_METHODS),
  score: z.number().int().nullable(),
  status: z.enum(RECONCILIATION_STATUSES),
  approvalReason: z.enum(RECONCILIATION_APPROVAL_REASONS).nullable(),
  proposedByUserId: z.uuid().nullable(),
  confirmedByUserId: z.uuid().nullable(),
  confirmedAt: z.string().nullable(),
  rejectedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  reversedAt: z.string().nullable(),
  reversalKind: z.enum(RECONCILIATION_REVERSAL_KINDS).nullable(),
  reversalReason: z.string().nullable(),
  createdAt: z.string(),
});

export type Reconciliation = z.infer<typeof reconciliationSchema>;

/**
 * A donation's match as its own reader sees it: the line's date, both amounts,
 * the account's label and the status — and nothing else about the line, whose
 * counterparty and description belong to the organization's accountants —
 * docs/decisions/0045-reconciliation-is-scoped-by-the-whole-organization.md.
 */
export const matchRateSchema = z.object({
  id: z.uuid(),
  currency: currencySchema,
  rateToBase: z.string(),
  rateDate: isoDaySchema,
});

export type MatchRate = z.infer<typeof matchRateSchema>;

export const matchMovementSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  institutionId: z.uuid().nullable(),
  institutionName: z.string().nullable(),
});

export const donationReconciliationSchema = z.object({
  id: z.uuid(),
  status: z.enum(RECONCILIATION_STATUSES),
  method: z.enum(RECONCILIATION_METHODS),
  bookedOn: isoDaySchema,
  accountLabel: z.string(),
  transactionAmount: moneySchema,
  donationAmount: moneySchema,
  rateDate: isoDaySchema.nullable(),
  /**
   * The rows the conversion used — null for a currency without a row (TRY) and
   * for every same-currency match (docs/decisions/0128). `rateToBase` is the
   * bulletin's rate as stored, a decimal string.
   */
  transactionRate: matchRateSchema.nullable(),
  donationRate: matchRateSchema.nullable(),
  /** What the match absorbed inside the FX tolerance, signed, in the donation's currency. */
  fxDifferenceMinor: signedMinorSchema,
  /**
   * Where the money came from: the movement, the organization whose account
   * holds it and the institution the movement's own text names, when it names
   * exactly one (docs/decisions/0119).
   */
  movement: matchMovementSchema,
  /** The movement's organization is none of those the donation's institution is linked to. */
  crossOrganization: z.boolean(),
  /**
   * Whether the reader may approve, reject, undo or reverse this match: they
   * hold the movement's organization (0045). A cross-organization match is
   * decided by the movement's side alone — it waits for them and never settles
   * on the donation side's word (docs/decisions/0128 §1).
   */
  mayAct: z.boolean(),
  /**
   * Whether the reader may take back a cross-organization proposal still
   * waiting for the movement's organization: they may act on the donation it
   * names. Once it is confirmed only the movement's side moves it.
   */
  mayWithdraw: z.boolean(),
  approvalReason: z.enum(RECONCILIATION_APPROVAL_REASONS).nullable(),
  /**
   * Who proposed a match that waits for a second person, so a screen can hide
   * the approval from the one user the four-eyes rule refuses it to.
   */
  proposedByUserId: z.uuid().nullable(),
  confirmedAt: z.string().nullable(),
  rejectedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  reversedAt: z.string().nullable(),
  reversalKind: z.enum(RECONCILIATION_REVERSAL_KINDS).nullable(),
  reversalReason: z.string().nullable(),
  createdAt: z.string(),
  /**
   * For a match of a payment typed by hand, where the payment stands — waiting
   * for a second person, approved and waiting for its bank line, found on its
   * statement, or approved cash; null for any other match, and for one no
   * longer waiting or confirmed (docs/decisions/0100).
   */
  manualPaymentStage: z.enum(MANUAL_PAYMENT_STAGES).nullable(),
});

export type DonationReconciliation = z.infer<typeof donationReconciliationSchema>;

/**
 * What the donation's reader may do about its money, decided on the server:
 * whether the caller's scope holds the donation's whole organization, whether
 * the donation can take another match at all, and what a match could still
 * credit it — the amount less its confirmed and pending matches, in its own
 * currency, or nothing when `closedReason` says its state takes no more money.
 */
export const donationAllocationSchema = z.object({
  organizationInScope: z.boolean(),
  /**
   * The reader may match this donation, but only to one of its **candidate**
   * lines — `reconciliation.donationCandidates` — and only whole: a `self`
   * reader, who holds no organization and reads no line list
   * (docs/decisions/0087). False wherever `organizationInScope` is true.
   */
  candidatesOnly: z.boolean(),
  acceptsMatch: z.boolean(),
  /**
   * Who may take this donation's money (docs/decisions/0112 §3): its own
   * organization once it has one, otherwise every organization its institution
   * is linked to — empty only when it is linked to none. `inScope` is whether
   * the reader's scope wholly holds that organization, the question every
   * match asks.
   */
  eligibleOrganizations: z.array(
    z.object({ id: z.uuid(), name: z.string(), inScope: z.boolean() }),
  ),
  outstanding: moneySchema,
  closedReason: z.enum(MATCH_CLOSED_REASONS).nullable(),
});

export type DonationAllocation = z.infer<typeof donationAllocationSchema>;

/**
 * A line of the donation's organization that can still take an allocation: a
 * non-excluded credit classified as a donor payment with something left.
 * Read by an accountant whose scope holds the whole organization, so the
 * description and counterparty are theirs to see.
 */
export const openLineSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationAccountId: z.uuid(),
  accountLabel: z.string(),
  bookedOn: isoDaySchema,
  amount: moneySchema,
  allocatedMinor: z.string(),
  remaining: moneySchema,
  description: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  organizationName: z.string(),
  equivalents: movementEquivalentsSchema,
});

export type OpenLine = z.infer<typeof openLineSchema>;

export const accountTransactionSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationAccountId: z.uuid(),
  source: z.enum(ACCOUNT_TRANSACTION_SOURCES),
  direction: z.enum(ACCOUNT_TRANSACTION_DIRECTIONS),
  amount: moneySchema,
  bookedOn: isoDaySchema,
  description: z.string().nullable(),
  classification: z.enum(ACCOUNT_TRANSACTION_CLASSIFICATIONS),
  allocatedMinor: z.string(),
  excludedAt: z.string().nullable(),
  excludedReason: z.string().nullable(),
  createdAt: z.string(),
});

export type AccountTransaction = z.infer<typeof accountTransactionSchema>;

/**
 * One donation of a match. **Either** `transactionAmountMinor` — what the
 * movement gives it, in the movement's currency — **or** `donationAmountMinor`,
 * only for a basket: what the basket takes from the donation, in the donation's
 * own currency, from which the server derives the movement-side amount under the
 * line's lock and never takes it from the client (docs/decisions/0131).
 */
export const matchAllocationSchema = z
  .object({
    donationId: z.uuid(),
    transactionAmountMinor: positiveMinorSchema.optional(),
    donationAmountMinor: positiveMinorSchema.optional(),
  })
  .refine(
    (allocation) =>
      (allocation.transactionAmountMinor === undefined) !==
      (allocation.donationAmountMinor === undefined),
    'an allocation names either the movement-side or the donation-side amount',
  );

export const matchInputSchema = z
  .object({
    accountTransactionId: z.uuid(),
    allocations: z
      .array(matchAllocationSchema)
      .min(1)
      .max(MAX_ALLOCATIONS_PER_MATCH)
      .refine(
        (allocations) =>
          new Set(allocations.map((allocation) => allocation.donationId)).size ===
          allocations.length,
        'a donation appears once in one match',
      ),
    expectedAllocatedMinor: nonNegativeMinorSchema,
    method: claimableReconciliationMethodSchema,
    proposalScore: z.number().int().min(0).max(100).optional(),
    /**
     * The person has been told that a donation they allocate is already paid by
     * a transfer or cheque typed by hand whose statement line has not been found,
     * on this line's account — so this line may be that payment's own money —
     * and matches anyway (`MANUAL_PAYMENT_UNCLEARED`, docs/decisions/0100).
     */
    acknowledgeUnclearedManual: z.boolean().optional(),
    /**
     * The match for a remainder takes the line whole — its remainder, nothing more
     * and nothing less. A change since the line was read is refused
     * `RECONCILIATION_BASKET_LINE_MISMATCH` under the line's lock rather than
     * leaving part of the line unallocated.
     */
    wholeLine: z.boolean().optional(),
    /**
     * The basket's rule: the allocations are the whole basket, and the line must
     * hold at least their sum unallocated, judged under the line's lock — a line
     * that holds less is refused `RECONCILIATION_BASKET_LINE_TOO_SMALL`. What the
     * basket leaves on the line is the remainder question's
     * (docs/decisions/0130).
     */
    basketLine: z.boolean().optional(),
  })
  .refine(
    (input) =>
      input.allocations.every((allocation) => allocation.donationAmountMinor === undefined) ||
      (input.basketLine === true &&
        input.allocations.every((allocation) => allocation.donationAmountMinor !== undefined)),
    'donation-side amounts belong to a basket and are sent for every donation of it',
  );

export type MatchInput = z.infer<typeof matchInputSchema>;

/** One allocation of a proposal, with what it credits the donation in the donation's own currency. */
export const proposedAllocationSchema = z.object({
  donationId: z.uuid(),
  referenceCode: z.string(),
  donorName: z.string().nullable(),
  transactionAmount: moneySchema,
  donationAmount: moneySchema,
  /** What the donation still owes before this proposal, in its own currency. */
  outstanding: moneySchema,
});

/**
 * The engine's reading of one line — docs/plans/05-finance-reconciliation.md
 * §5.5's tier table. A read, never a row: nothing is stored or reserved, and
 * the writer judges every figure again under its lock. `rateDate` is set only
 * for a cross-currency proposal, which waits for a second person.
 */
export const matchProposalSchema = z.object({
  method: z.enum(RECONCILIATION_METHODS),
  score: z.number().int(),
  bulkConfirmable: z.boolean(),
  approvalReason: z.enum(RECONCILIATION_APPROVAL_REASONS).nullable(),
  rateDate: isoDaySchema.nullable(),
  allocations: z.array(proposedAllocationSchema),
});

export type MatchProposalView = z.infer<typeof matchProposalSchema>;

/**
 * One line of the proposals list. A line the engine reads as the bank's side
 * of a payment typed by hand is proposed **no donation** — its money was
 * counted when the payment was approved — and is listed with `proposal: null`
 * and how many such payments it may be, so it says why it waits instead of
 * disappearing (docs/decisions/0100). It is confirmed from its drawer, one at a
 * time, never in bulk.
 */
export const lineProposalSchema = z.object({
  line: openLineSchema,
  proposal: matchProposalSchema.nullable(),
  clearingCandidates: z.number().int().min(0),
});

export type LineProposal = z.infer<typeof lineProposalSchema>;

export const proposalsInputSchema = paginationInputSchema
  .extend({
    organizationAccountId: z.uuid().optional(),
    statementImportId: z.uuid().optional(),
  })
  .refine(
    (input) =>
      (input.organizationAccountId === undefined) !== (input.statementImportId === undefined),
    'name exactly one of an account or a statement import',
  );

export type ProposalsInput = z.infer<typeof proposalsInputSchema>;

/** A donation that can take part of a line, with what it still owes and why it is offered. */
export const matchCandidateSchema = z.object({
  donationId: z.uuid(),
  referenceCode: z.string(),
  donorName: z.string().nullable(),
  campaignName: z.string(),
  institutionName: z.string(),
  donatedAt: z.string(),
  status: donationStatusSchema,
  paymentStatus: paymentStatusSchema,
  amount: moneySchema,
  outstanding: moneySchema,
  reading: z.enum(CANDIDATE_READINGS).nullable(),
});

export type MatchCandidateView = z.infer<typeof matchCandidateSchema>;

/**
 * A donation the line names that cannot take its money, and why. Shown, never
 * offered: the money stays unmatched for a person to decide about —
 * docs/decisions/0023-donation-status-axes.md.
 */
export const informationHitSchema = z.object({
  donationId: z.uuid(),
  referenceCode: z.string(),
  reason: z.enum(INFORMATION_HIT_REASONS),
});

export type InformationHit = z.infer<typeof informationHitSchema>;

export const candidatesInputSchema = paginationInputSchema.extend({
  accountTransactionId: z.uuid(),
  referenceCode: z.string().trim().max(20).optional(),
  donor: z.string().trim().max(100).optional(),
  minAmountMinor: nonNegativeMinorSchema.optional(),
  maxAmountMinor: nonNegativeMinorSchema.optional(),
  institutionId: z.uuid().optional(),
});

export type CandidatesInput = z.infer<typeof candidatesInputSchema>;

/**
 * A payment typed from a slip or a cheque that this statement line may be the
 * bank's side of — the matcher's pre-tier, shown before any donation
 * (docs/decisions/0100). Confirming it holds the line for the payment and
 * moves no money, because the payment was counted when it was approved.
 */
export const clearingCandidateSchema = z.object({
  manualTransactionId: z.uuid(),
  reading: z.enum(CLEARING_READINGS),
  score: z.number().int(),
  exactAmount: z.boolean(),
  amount: moneySchema,
  receivedOn: isoDaySchema,
  method: z.enum(MANUAL_PAYMENT_METHODS),
  payerName: z.string().nullable(),
  reference: z.string().nullable(),
  recordedByName: z.string().nullable(),
  donations: z.array(
    z.object({ donationId: z.uuid(), referenceCode: z.string(), donorName: z.string().nullable() }),
  ),
});

export type ClearingCandidate = z.infer<typeof clearingCandidateSchema>;

export const lineCandidatesSchema = z.object({
  line: openLineSchema,
  /**
   * The payments typed by hand this line may be the money of, strongest first.
   * When there is one, the engine proposes no donation for the line and bulk
   * confirmation passes it by, so its money is not counted twice.
   */
  clearings: z.array(clearingCandidateSchema),
  proposal: matchProposalSchema.nullable(),
  /** The donations the line itself points at — by code, by a one-symbol typo, or by amount and date. */
  readings: z.array(matchCandidateSchema),
  information: z.array(informationHitSchema),
  /** A person's own search among the organization's donations, when one was asked. */
  search: paginated(matchCandidateSchema).nullable(),
  institutions: z.array(z.object({ id: z.uuid(), name: z.string() })),
});

export type LineCandidates = z.infer<typeof lineCandidatesSchema>;

export const conversionPreviewSchema = z.object({
  transactionAmount: moneySchema,
  /** What the donation is credited: the converted amount, or its whole outstanding when the difference is inside the tolerance. */
  donationAmount: moneySchema.nullable(),
  /** The bulletin day of the movement's frozen rows, null for a same-currency match. */
  rateDate: isoDaySchema.nullable(),
  outstanding: moneySchema,
  /** What the match would absorb inside the tolerance, signed, in the donation's currency. */
  fxDifferenceMinor: signedMinorSchema,
  /** The FX tolerance in force, in basis points — 100 is 1%. */
  fxToleranceBasisPoints: z.number().int(),
  /**
   * The movement-side amount that converts to exactly the donation's
   * outstanding (the smallest one that does), capped at what the movement has
   * left — null when the movement cannot cover it or the conversion has no
   * rate. The dialog starts from it so a surplus stays on the movement for the
   * remainder question.
   */
  suggestedTransactionAmount: moneySchema.nullable(),
  refusal: z.string().nullable(),
});

export type ConversionPreview = z.infer<typeof conversionPreviewSchema>;

export const MAX_BULK_CONFIRM_ITEMS = 500;

/**
 * The per-row answers of a bulk confirmation. A row that is no longer an exact
 * reference match when the server reads it again is
 * `RECONCILIATION_NOT_BULK_CONFIRMABLE`; one whose allocation moved since the
 * screen read it is `RECONCILIATION_LINE_CHANGED`.
 */
export const BULK_CONFIRM_CODES = [
  'NOT_FOUND',
  'OUT_OF_SCOPE',
  'RECONCILIATION_NOT_BULK_CONFIRMABLE',
  'RECONCILIATION_LINE_CHANGED',
  'RECONCILIATION_LINE_NOT_MATCHABLE',
  'RECONCILIATION_DONATION_NOT_MATCHABLE',
  'RECONCILIATION_OVER_ALLOCATED',
  'RECONCILIATION_EXCEEDS_OUTSTANDING',
  'MANUAL_PAYMENT_UNCLEARED',
] as const;

export const bulkConfirmResultSchema = bulkRowSchema(BULK_CONFIRM_CODES);

export type BulkConfirmResult = z.infer<typeof bulkConfirmResultSchema>;

export const bulkConfirmInputSchema = z.object({
  items: z
    .array(
      z.object({ accountTransactionId: z.uuid(), expectedAllocatedMinor: nonNegativeMinorSchema }),
    )
    .min(1)
    .max(MAX_BULK_CONFIRM_ITEMS)
    .refine(
      (items) => new Set(items.map((item) => item.accountTransactionId)).size === items.length,
      'a line appears once in one confirmation',
    ),
});

export type BulkConfirmInput = z.infer<typeof bulkConfirmInputSchema>;

/**
 * Every incoming movement that still has money left, of every organization
 * (docs/decisions/0126). `organizationId` narrows to one; leaving it out lists
 * all. `search` reads the counterparty, description and reference; the amount
 * filter reads the original amount or an equivalent. `amountMinor` keeps only
 * the movements with at least that much unallocated, in one currency — the
 * basket's rule (docs/decisions/0130); no screen sends it.
 */
export const matchableLinesInputSchema = paginationInputSchema
  .extend({
    organizationId: z.uuid().optional(),
    search: z.string().trim().max(200).optional(),
    amountMinor: positiveMinorSchema.optional(),
    ...movementAmountFilterShape,
  })
  .superRefine(refineMovementAmountFilter)
  .refine(
    (input) => input.amountMinor === undefined || input.currency !== undefined,
    'an amount is asked in one currency',
  );

export type MatchableLinesInput = z.infer<typeof matchableLinesInputSchema>;

/**
 * The two line lists of the reconciliation screen beside its proposals, named
 * as `accountTransaction.list` names them: `open` is the exceptions queue —
 * not excluded and something left, in either direction and every
 * classification — and `excluded` the lines taken out of it.
 */
export const RECONCILIATION_LINE_ALLOCATIONS = ['open', 'excluded'] as const;

export const accountLinesInputSchema = paginationInputSchema.extend({
  organizationAccountId: z.uuid(),
  allocation: z.enum(RECONCILIATION_LINE_ALLOCATIONS),
});

export type AccountLinesInput = z.infer<typeof accountLinesInputSchema>;

export const settleRemainderInputSchema = z.object({
  accountTransactionId: z.uuid(),
  /** The donation the movement was just matched to: its donor is the new donation's donor. */
  sourceDonationId: z.uuid(),
  /** A free-amount campaign accepting staff entry; the server picks its open period. */
  campaignId: z.uuid(),
  expectedAllocatedMinor: nonNegativeMinorSchema,
});

export type SettleRemainderInput = z.infer<typeof settleRemainderInputSchema>;

export const remainderCampaignsInputSchema = z.object({
  /** The movement whose remainder is asked about: its currency and what is left are what each campaign is priced for. */
  accountTransactionId: z.uuid(),
  /** The donation the movement was matched to: the remainder donation is recorded at its institution. */
  sourceDonationId: z.uuid(),
});

export type RemainderCampaignsInput = z.infer<typeof remainderCampaignsInputSchema>;

/** A campaign the remainder's donation may be opened in — nothing of its configuration, owner or totals. */
export const remainderCampaignSchema = z.object({ id: z.uuid(), name: z.string() });

export type RemainderCampaign = z.infer<typeof remainderCampaignSchema>;

export const FX_TOLERANCE_MAX_BASIS_POINTS = 1000;

export const reconciliationSettingsSchema = z.object({
  /** What a cross-currency match may leave over or short as a share of what the donation owes and still settle it; 100 is 1%. */
  fxToleranceBasisPoints: z.number().int().min(0).max(FX_TOLERANCE_MAX_BASIS_POINTS),
  updatedAt: z.string(),
});

export type ReconciliationSettings = z.infer<typeof reconciliationSettingsSchema>;

export const updateReconciliationSettingsInputSchema = z.object({
  fxToleranceBasisPoints: z.number().int().min(0).max(FX_TOLERANCE_MAX_BASIS_POINTS),
});

export const MAX_BASKET_DONATIONS = 50;

export const BASKET_REFUSALS = [
  'RECONCILIATION_BASKET_MIXED_CURRENCIES',
  'RECONCILIATION_BASKET_NOT_MATCHABLE',
  'RECONCILIATION_BASKET_EXCEEDS_OUTSTANDING',
] as const;

/**
 * What a person typed for one donation of the basket — less than it owes,
 * when the payment covers only part of it, as the legacy `odeme_sepeti` let the
 * accountant type a figure per donation, capped at what was left. A donation
 * the basket names no amount for takes its whole outstanding.
 */
export const basketAmountSchema = z.object({
  donationId: z.uuid(),
  amountMinor: positiveMinorSchema,
});

export type BasketAmount = z.infer<typeof basketAmountSchema>;

export const basketInputSchema = z
  .object({
    donationIds: z.array(z.uuid()).min(1).max(MAX_BASKET_DONATIONS),
    amounts: z.array(basketAmountSchema).max(MAX_BASKET_DONATIONS).optional(),
  })
  .refine(
    (input) =>
      (input.amounts ?? []).every((amount) => input.donationIds.includes(amount.donationId)),
    'an amount is typed only for a donation in the basket',
  )
  .refine(
    (input) =>
      new Set((input.amounts ?? []).map((amount) => amount.donationId)).size ===
      (input.amounts ?? []).length,
    'a donation takes one amount',
  );

export type BasketInput = z.infer<typeof basketInputSchema>;

export const basketDonationSchema = z.object({
  donationId: z.uuid(),
  referenceCode: z.string(),
  donorName: z.string().nullable(),
  campaignName: z.string(),
  institutionName: z.string(),
  organizationId: z.uuid().nullable(),
  amount: moneySchema,
  outstanding: moneySchema,
  /**
   * What the basket takes from it: the amount typed for it, or its whole
   * outstanding. Never more than `outstanding` without `exceedsOutstanding`
   * saying so, and the match refuses it again under the donation's lock.
   */
  allocation: moneySchema,
  exceedsOutstanding: z.boolean(),
  information: z.enum(INFORMATION_HIT_REASONS).nullable(),
});

/**
 * The legacy `odeme_sepeti`, kept in the URL rather than a PHP session: the
 * donations a person picked, what the basket takes from each, the `total` of
 * those allocations — summed here, never by the screen — and whether one line
 * can settle them at all: one currency, every one of them able to take money,
 * and no amount above what its donation owes. Donations of any organization
 * may share a basket (docs/decisions/0128).
 */
export const basketSchema = z.object({
  items: z.array(basketDonationSchema),
  total: moneySchema.nullable(),
  refusal: z.enum(BASKET_REFUSALS).nullable(),
});

export type Basket = z.infer<typeof basketSchema>;

export const BASKET_FIT_REFUSALS = [
  'RECONCILIATION_LINE_NOT_MATCHABLE',
  'RECONCILIATION_MOVEMENT_RATE_PENDING',
  'RECONCILIATION_RATE_UNAVAILABLE',
  'RECONCILIATION_EXCEEDS_OUTSTANDING',
  'RECONCILIATION_BASKET_LINE_TOO_SMALL',
] as const;

export const MAX_BASKET_FIT_LINES = 100;

export const basketFitInputSchema = z
  .object({
    donationIds: z.array(z.uuid()).min(1).max(MAX_BASKET_DONATIONS),
    amounts: z.array(basketAmountSchema).max(MAX_BASKET_DONATIONS).optional(),
    lineIds: z.array(z.uuid()).min(1).max(MAX_BASKET_FIT_LINES),
  })
  .refine(
    (input) =>
      (input.amounts ?? []).every((amount) => input.donationIds.includes(amount.donationId)),
    'an amount is typed only for a donation in the basket',
  )
  .refine(
    (input) =>
      new Set((input.amounts ?? []).map((amount) => amount.donationId)).size ===
      (input.amounts ?? []).length,
    'a donation takes one amount',
  );

export type BasketFitInput = z.infer<typeof basketFitInputSchema>;

/**
 * What the basket needs of one candidate movement, decided by the server with
 * the figures the write will use (docs/decisions/0131): `needed` is the sum of
 * the movement-side amounts that credit each donation its basket amount, in the
 * movement's currency, on the movement's frozen bulletin (`rateDate`), and
 * `covers` whether the movement's unallocated `remaining` holds it. A line the
 * basket cannot use says why in `refusal`; the screen converts nothing.
 */
export const basketLineFitSchema = z.object({
  lineId: z.uuid(),
  remaining: moneySchema.nullable(),
  needed: moneySchema.nullable(),
  rateDate: isoDaySchema.nullable(),
  covers: z.boolean(),
  refusal: z.enum(BASKET_FIT_REFUSALS).nullable(),
});

export type BasketLineFit = z.infer<typeof basketLineFitSchema>;

export const basketFitSchema = z.object({ items: z.array(basketLineFitSchema) });

export type BasketFit = z.infer<typeof basketFitSchema>;

export const reconciliationListItemSchema = z.object({
  id: z.uuid(),
  status: z.enum(RECONCILIATION_STATUSES),
  method: z.enum(RECONCILIATION_METHODS),
  score: z.number().int().nullable(),
  approvalReason: z.enum(RECONCILIATION_APPROVAL_REASONS).nullable(),
  rateDate: isoDaySchema.nullable(),
  transactionAmount: moneySchema,
  donationAmount: moneySchema,
  proposedByUserId: z.uuid().nullable(),
  proposedByName: z.string().nullable(),
  /** Who confirmed it — the second person of a cross-currency match, else the one who matched. */
  confirmedByName: z.string().nullable(),
  createdAt: z.string(),
  confirmedAt: z.string().nullable(),
  rejectedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  reversedAt: z.string().nullable(),
  reversalKind: z.enum(RECONCILIATION_REVERSAL_KINDS).nullable(),
  reversalReason: z.string().nullable(),
  line: z.object({
    id: z.uuid(),
    bookedOn: isoDaySchema,
    accountLabel: z.string(),
    description: z.string().nullable(),
    counterpartyName: z.string().nullable(),
  }),
  donation: z.object({
    id: z.uuid(),
    referenceCode: z.string(),
    donorName: z.string().nullable(),
    amount: moneySchema,
    /** The institution that recorded the donation — whose side proposed a cross-organization match. */
    institutionName: z.string(),
  }),
  /**
   * The movement's organization is none of those the donation's institution is
   * linked to: a pending one waits for this organization alone and settles
   * nothing until it is confirmed (docs/decisions/0128 §1).
   */
  crossOrganization: z.boolean(),
});

export type ReconciliationListItem = z.infer<typeof reconciliationListItemSchema>;

/**
 * What became of a match nobody is still deciding — the history tab's filter.
 * `reversed` and `unmatched` are both the `reversed` status: an unmatch is a
 * person taking a match back, every other reversal is money leaving (a
 * chargeback, a cancellation's refund or reverse, a provider refund).
 */
export const RECONCILIATION_OUTCOMES = ['confirmed', 'rejected', 'reversed', 'unmatched'] as const;

export type ReconciliationOutcome = (typeof RECONCILIATION_OUTCOMES)[number];

/**
 * `decided` lists every match no longer waiting for a second person — the
 * history of what was matched, rejected and taken back. The dates are the
 * bank line's booking day; `search` is a donation's reference code, with or
 * without its `BT`, or its donor's name.
 */
export const reconciliationListInputSchema = paginationInputSchema.extend({
  organizationAccountId: z.uuid().optional(),
  status: z.enum(RECONCILIATION_STATUSES).optional(),
  decided: queryBooleanSchema.optional(),
  outcome: z.enum(RECONCILIATION_OUTCOMES).optional(),
  bookedFrom: isoDaySchema.optional(),
  bookedTo: isoDaySchema.optional(),
  search: z.string().trim().max(100).optional(),
});

export type ReconciliationListInput = z.infer<typeof reconciliationListInputSchema>;

/**
 * The history tab's filter, without its page: every match nobody is still
 * deciding that the filter names, as a file the worker writes —
 * docs/decisions/0043-the-worker.md §7. `decided` is not an input: the build
 * always reads the history, never the approvals inbox.
 */
export const reconciliationHistoryExportInputSchema = reconciliationListInputSchema.omit({
  page: true,
  pageSize: true,
  status: true,
  decided: true,
});

export type ReconciliationHistoryExportInput = z.infer<
  typeof reconciliationHistoryExportInputSchema
>;

export const shortfallSchema = z.object({
  donationId: z.uuid(),
  referenceCode: z.string(),
  donorName: z.string().nullable(),
  campaignName: z.string(),
  institutionName: z.string(),
  amount: moneySchema,
  collected: moneySchema,
  outstanding: moneySchema,
  lastMatchedAt: z.string().nullable(),
  lastMatchAgeDays: z.number().int().nullable(),
  matches: z.array(
    z.object({
      id: z.uuid(),
      accountTransactionId: z.uuid(),
      bookedOn: isoDaySchema,
      accountLabel: z.string(),
      transactionAmount: moneySchema,
      donationAmount: moneySchema,
    }),
  ),
});

export type Shortfall = z.infer<typeof shortfallSchema>;

export const shortfallsInputSchema = paginationInputSchema.extend({
  organizationId: z.uuid(),
  currency: currencySchema.optional(),
  olderThanDays: z.coerce.number().int().min(0).max(3650).optional(),
});

export type ShortfallsInput = z.infer<typeof shortfallsInputSchema>;

/** The shortfall report's filter, without its page: an export is every matching row. */
export const shortfallsExportInputSchema = shortfallsInputSchema.omit({
  page: true,
  pageSize: true,
});

export type ShortfallsExportInput = z.infer<typeof shortfallsExportInputSchema>;

export const LINE_MATCH_ACTIONS = ['approve', 'reject', 'unmatch', 'reverse'] as const;

export type LineMatchAction = (typeof LINE_MATCH_ACTIONS)[number];

/**
 * One match a line paid, as the line's own reader sees it, with the moves the
 * server says this reader may make on it — the match's state, the caller's
 * organization scope and the four-eyes rule, never the caller's roles. The
 * screen narrows them only by the permissions it holds; each write asks again.
 */
export const lineMatchUseSchema = z.object({
  id: z.uuid(),
  status: z.enum(RECONCILIATION_STATUSES),
  method: z.enum(RECONCILIATION_METHODS),
  transactionAmount: moneySchema,
  donationAmount: moneySchema,
  rateDate: isoDaySchema.nullable(),
  donation: z.object({
    id: z.uuid(),
    referenceCode: z.string(),
    donorName: z.string().nullable(),
  }),
  proposedByName: z.string().nullable(),
  confirmedByName: z.string().nullable(),
  createdAt: z.string(),
  confirmedAt: z.string().nullable(),
  rejectedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  reversedAt: z.string().nullable(),
  reversalKind: z.enum(RECONCILIATION_REVERSAL_KINDS).nullable(),
  reversalReason: z.string().nullable(),
  actions: z.array(z.enum(LINE_MATCH_ACTIONS)),
});

export type LineMatchUse = z.infer<typeof lineMatchUseSchema>;

/** A refund the line is the source of — money it brought that is owed back — or the payment of. */
export const lineRefundUseSchema = z.object({
  id: z.uuid(),
  role: z.enum(['source', 'paid']),
  status: z.enum(REFUND_STATUSES),
  amount: moneySchema,
  reason: z.string(),
  donation: z.object({ id: z.uuid(), referenceCode: z.string() }).nullable(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
});

export type LineRefundUse = z.infer<typeof lineRefundUseSchema>;

/**
 * A fund transfer the line carries: `source` is a pass-through's incoming
 * credit, `out` the debit on the giving side, `in` the credit on the receiving one.
 */
export const lineFundTransferUseSchema = z.object({
  id: z.uuid(),
  side: z.enum(['source', 'out', 'in']),
  status: z.enum(FUND_TRANSFER_STATUSES),
  amount: moneySchema,
  fromOrganizationName: z.string(),
  toOrganizationName: z.string(),
  reason: z.string(),
  createdAt: z.string(),
});

export type LineFundTransferUse = z.infer<typeof lineFundTransferUseSchema>;

/** A card payout the line is the bank side of, or a capture the payout paid out. */
export const linePayoutUseSchema = z.object({
  id: z.uuid(),
  role: z.enum(['bank', 'capture']),
  status: z.enum(['confirmed', 'reversed']),
  provider: z.enum(CARD_PROVIDERS),
  gross: moneySchema,
  fee: moneySchema,
  confirmedAt: z.string(),
  reversedAt: z.string().nullable(),
  released: z.boolean(),
});

export type LinePayoutUse = z.infer<typeof linePayoutUseSchema>;

export const LINE_CLEARING_ACTIONS = ['unclear'] as const;

export const MANUAL_PAYMENT_CLEARING_STATUSES = ['confirmed', 'reversed'] as const;

/**
 * A payment typed from a slip or a cheque, found on its bank statement —
 * docs/decisions/0100. Read from either side: `bank` on the statement line it
 * holds, `manual` on the payment's own line. `otherLine` is the line on the
 * other side. Undoing it is `unclear`, offered while it is confirmed; the
 * screen narrows it by `reconciliation.unmatch`, and the write asks again.
 */
export const lineClearingUseSchema = z.object({
  id: z.uuid(),
  role: z.enum(['bank', 'manual']),
  status: z.enum(MANUAL_PAYMENT_CLEARING_STATUSES),
  amount: moneySchema,
  otherLine: z.object({
    id: z.uuid(),
    bookedOn: isoDaySchema,
    accountLabel: z.string(),
    description: z.string().nullable(),
    reference: z.string().nullable(),
  }),
  confirmedByName: z.string().nullable(),
  confirmedAt: z.string(),
  reversedByName: z.string().nullable(),
  reversedAt: z.string().nullable(),
  reversalReason: z.string().nullable(),
  actions: z.array(z.enum(LINE_CLEARING_ACTIONS)),
});

export type LineClearingUse = z.infer<typeof lineClearingUseSchema>;

/**
 * Everything that took part of one line, so the line's page says where its
 * money went: every match with its donation, every refund, every fund-transfer
 * side, every card payout and every clearing of a payment typed by hand, and
 * who excluded it and why. `mayInclude` is the server's answer for putting an
 * excluded line back.
 */
export const lineUsesSchema = z.object({
  lineId: z.uuid(),
  matches: z.array(lineMatchUseSchema),
  refunds: z.array(lineRefundUseSchema),
  fundTransfers: z.array(lineFundTransferUseSchema),
  payouts: z.array(linePayoutUseSchema),
  clearings: z.array(lineClearingUseSchema),
  exclusion: z
    .object({
      at: z.string(),
      reason: z.string().nullable(),
      classification: z.enum(ACCOUNT_TRANSACTION_CLASSIFICATIONS),
      byName: z.string().nullable(),
    })
    .nullable(),
  mayInclude: z.boolean(),
});

export type LineUses = z.infer<typeof lineUsesSchema>;

export const reconciliationAccountSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  kind: z.enum(['bank', 'paypal', 'pos', 'cash', 'stripe']),
  currency: currencySchema,
  organizationId: z.uuid(),
  organizationName: z.string(),
});

export type ReconciliationAccount = z.infer<typeof reconciliationAccountSchema>;

export const exclusionInputSchema = z.object({
  id: z.uuid(),
  classification: z.enum(EXCLUSION_CLASSIFICATIONS),
  reason: reasonSchema,
});

export type ExclusionInput = z.infer<typeof exclusionInputSchema>;

/**
 * The allocation writer — docs/plans/05-finance-reconciliation.md §5.3. Every
 * move of `payment_status`, `collected` and the ledger goes through these
 * routes, and refusals arrive as `details.reason` under `CONFLICT` or
 * `VALIDATION_FAILED`.
 */
export const reconciliationContract = {
  /**
   * The server computes every donation-side amount and every conversion; the
   * client names only how much of the line goes where, and the allocation it
   * last saw, so a line that moved underneath it is refused rather than
   * over-allocated.
   */
  match: oc
    .route({ method: 'POST', path: '/reconciliations', summary: 'Allocate a line to donations' })
    .errors(guarded)
    .input(matchInputSchema)
    .output(
      z.object({
        matches: z.array(reconciliationSchema),
        accountTransaction: z.object({
          id: z.uuid(),
          amount: moneySchema,
          allocatedMinor: z.string(),
        }),
        /**
         * What is left of the movement, in the movement's currency, when this
         * match left every donation it named owing nothing and the movement
         * still holds money — the amount the remainder question is about
         * (docs/decisions/0128 §6). Null otherwise.
         */
        remainder: moneySchema.nullable(),
      }),
    ),

  /**
   * The remainder question's *yes* (docs/decisions/0128 §6): a donation for the
   * same donor as `sourceDonationId`, in the movement's currency and for what is
   * left of the movement, in a free-amount campaign — made through the staff
   * entry path with all its gates — and matched to that movement through the
   * match path. Behind `reconciliation.match` alone — the donation takes its
   * source's ownership, so it is the matcher's act, not their own entry
   * (docs/decisions/0132).
   */
  settleRemainder: oc
    .route({ method: 'POST', path: '/reconciliations/remainder' })
    .errors(guarded)
    .input(settleRemainderInputSchema)
    .output(
      z.object({
        donationId: z.uuid(),
        referenceCode: z.string(),
        matches: z.array(reconciliationSchema),
        accountTransaction: z.object({
          id: z.uuid(),
          amount: moneySchema,
          allocatedMinor: z.string(),
        }),
      }),
    ),

  /**
   * The campaigns the remainder question offers: free-amount, with a season
   * open now, in which a donation of the movement's currency and remainder,
   * recorded at the source donation's institution, would be accepted — each
   * judged by the save's own preparation, so the list offers what the write
   * accepts. Behind `reconciliation.match`; the source is read through the
   * caller's donation scope, `NOT_FOUND` outside it, and the movement is
   * refused as the write refuses it (docs/decisions/0132).
   */
  remainderCampaigns: oc
    .route({ method: 'GET', path: '/reconciliations/remainder-campaigns' })
    .errors(guarded)
    .input(remainderCampaignsInputSchema)
    .output(z.object({ items: z.array(remainderCampaignSchema) })),

  unmatch: oc
    .route({ method: 'POST', path: '/reconciliations/{id}/unmatch' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(z.object({ match: reconciliationSchema })),

  /** A cross-currency match waits for a second person; the proposer may not approve it. */
  approve: oc
    .route({ method: 'POST', path: '/reconciliations/{id}/approve' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ match: reconciliationSchema })),

  reject: oc
    .route({ method: 'POST', path: '/reconciliations/{id}/reject' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(z.object({ match: reconciliationSchema })),

  /** A chargeback reported by a bank notice. The line stays allocated. */
  reverse: oc
    .route({ method: 'POST', path: '/reconciliations/{id}/reverse' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(z.object({ match: reconciliationSchema })),

  /** Read through donation scope, as `donation.get` is. */
  byDonation: oc
    .route({ method: 'GET', path: '/donations/{donationId}/reconciliations' })
    .errors(guarded)
    .input(z.object({ donationId: z.uuid() }))
    .output(
      z.object({
        items: z.array(donationReconciliationSchema),
        allocation: donationAllocationSchema,
      }),
    ),

  /**
   * The lines that fit one donation — holding nothing, in its currency, of
   * exactly what it still owes — read through **donation** scope, for the reader
   * `allocation.candidatesOnly` names: they may pick one and read no other line
   * of the organization (docs/decisions/0087). Refused `OUT_OF_SCOPE` to any
   * other reader of the donation who does not wholly hold its organization.
   */
  donationCandidates: oc
    .route({ method: 'GET', path: '/donations/{donationId}/reconciliation-candidates' })
    .errors(guarded)
    .input(paginationInputSchema.extend({ donationId: z.uuid() }))
    .output(paginated(openLineSchema)),

  /**
   * An organization's lines that can still take money, newest first — and,
   * given a currency and an amount, only the lines holding no allocation whose
   * amount is exactly it, which is the basket's question: a basket settles its
   * donations from one payment nobody has matched yet. Every organization's
   * incoming movements are listed (docs/decisions/0126); each says whether the
   * caller's scope holds its organization, and a match asks again.
   */
  matchableLines: oc
    .route({ method: 'GET', path: '/reconciliations/lines' })
    .errors(guarded)
    .input(matchableLinesInputSchema)
    .output(paginated(openLineSchema)),

  /**
   * One account's exceptions queue or its excluded lines, newest first — the
   * reconciliation screen's unmatched and excluded tabs, read under that
   * screen's permission rather than the statements screen's, and refused for
   * an account whose organization the caller's scope does not wholly hold.
   */
  accountLines: oc
    .route({ method: 'GET', path: '/reconciliations/account-lines' })
    .errors(guarded)
    .input(accountLinesInputSchema)
    .output(paginated(accountTransactionListItemSchema)),

  /** Every active account of an organization the caller's scope wholly holds. */
  accounts: oc
    .route({ method: 'GET', path: '/reconciliations/accounts' })
    .errors(guarded)
    .input(paginationInputSchema)
    .output(paginated(reconciliationAccountSchema)),

  /** The engine's best proposal for every open line of an account or an import that has one. */
  proposals: oc
    .route({ method: 'GET', path: '/reconciliations/proposals' })
    .errors(guarded)
    .input(proposalsInputSchema)
    .output(paginated(lineProposalSchema)),

  /** One line read by the engine, what it cannot offer and why, and a person's own search. */
  candidates: oc
    .route({ method: 'GET', path: '/reconciliations/candidates' })
    .errors(guarded)
    .input(candidatesInputSchema)
    .output(lineCandidatesSchema),

  /** What part of a line would credit a donation, converted on the rate day the writer will use. */
  conversion: oc
    .route({ method: 'GET', path: '/reconciliations/conversion' })
    .errors(guarded)
    .input(
      z.object({
        accountTransactionId: z.uuid(),
        donationId: z.uuid(),
        transactionAmountMinor: positiveMinorSchema,
      }),
    )
    .output(conversionPreviewSchema),

  /**
   * Exact reference matches confirmed by one person — docs/decisions/0044 §3 —
   * one transaction per line, with a result per line in request order.
   */
  bulkConfirm: oc
    .route({ method: 'POST', path: '/reconciliations/bulk-confirm' })
    .errors(guarded)
    .input(bulkConfirmInputSchema)
    .output(bulkResultSchema(bulkConfirmResultSchema)),

  /**
   * The basket, with the amount typed for each donation that pays only part of
   * what it owes. A read: the match it leads to judges every amount again
   * under the locks.
   */
  basket: oc
    .route({ method: 'GET', path: '/reconciliations/basket' })
    .errors(guarded)
    .input(basketInputSchema)
    .output(basketSchema),

  /**
   * What the basket needs of each candidate movement (docs/decisions/0131) —
   * for a movement in another currency, the sum of the movement-side amounts
   * that credit each donation its basket amount on the movement's frozen
   * bulletin, and whether the movement holds it. A read: the match derives the
   * same figures again under the line's lock.
   */
  basketFit: oc
    .route({ method: 'GET', path: '/reconciliations/basket-fit' })
    .errors(guarded)
    .input(basketFitInputSchema)
    .output(basketFitSchema),

  /** Matches of the organizations in scope — with `status=pending_approval`, the approvals inbox. */
  list: oc
    .route({ method: 'GET', path: '/reconciliations' })
    .errors(guarded)
    .input(reconciliationListInputSchema)
    .output(paginated(reconciliationListItemSchema)),

  /** The FX tolerance cross-currency matches absorb — docs/decisions/0128. */
  settings: oc
    .route({ method: 'GET', path: '/reconciliations/settings' })
    .errors(guarded)
    .input(z.object({}))
    .output(reconciliationSettingsSchema),

  /** Global scope alone, behind `reconciliation.settings`; the old and new tolerance are audited. */
  updateSettings: oc
    .route({ method: 'PUT', path: '/reconciliations/settings' })
    .errors(guarded)
    .input(updateReconciliationSettingsInputSchema)
    .output(reconciliationSettingsSchema),

  /** Every partly paid donation of an organization. A same-currency transfer that arrived short stays here — docs/decisions/0044 §5. */
  shortfalls: oc
    .route({ method: 'GET', path: '/reconciliations/shortfalls' })
    .errors(guarded)
    .input(shortfallsInputSchema)
    .output(paginated(shortfallSchema)),

  /** The whole shortfall filter as a file the worker writes — docs/decisions/0043-the-worker.md §7. */
  exportShortfalls: oc
    .route({ method: 'POST', path: '/reconciliations/shortfalls/export' })
    .errors(guarded)
    .input(shortfallsExportInputSchema)
    .output(generatedFileSchema),

  /**
   * The history tab's whole filter as a file the worker writes — behind
   * `report.export` and `reconciliation.list`, checked at the request and again
   * when the job runs.
   */
  exportHistory: oc
    .route({ method: 'POST', path: '/reconciliations/history/export' })
    .errors(guarded)
    .input(reconciliationHistoryExportInputSchema)
    .output(generatedFileSchema),
};

/**
 * A line's moves outside matching. Cash is typed through
 * `manualPayment.record`, which a second person approves —
 * docs/decisions/0100; the one-person `createCash` route was removed.
 */
export const accountTransactionContract = {
  /** Takes a line out of the exceptions queue as something other than donation money. */
  exclude: oc
    .route({ method: 'POST', path: '/account-transactions/{id}/exclude' })
    .errors(guarded)
    .input(exclusionInputSchema)
    .output(accountTransactionSchema),

  /**
   * Where one line's money went — docs/plans/05-finance-reconciliation.md
   * §5.11. Behind the line page's own permission, `statement.view`, and scoped
   * by the line's whole organization as `accountTransaction.get` is.
   */
  uses: oc
    .route({ method: 'GET', path: '/account-transactions/{id}/uses' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(lineUsesSchema),

  /** Puts an excluded line back in the exceptions queue. */
  include: oc
    .route({ method: 'POST', path: '/account-transactions/{id}/include' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(accountTransactionSchema),

  /**
   * Names what an outgoing line is — a refund's payout, a transfer, a fee —
   * before anything takes it. A credit is classified by what it pays and is
   * not reclassified here.
   */
  classify: oc
    .route({ method: 'POST', path: '/account-transactions/{id}/classification' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), classification: z.enum(OUTGOING_CLASSIFICATIONS) }))
    .output(accountTransactionSchema),
};
